'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database,
  Lightning,
  Terminal,
  Table,
  DownloadSimple,
  CaretLeft,
  CaretRight,
  ArrowsLeftRight,
} from '@phosphor-icons/react';
import { useUploadedFileStore } from '../../stores/uploadedFileStore';
import { useWorkflowStore } from '../../stores/workflowStore';
import { DiffResultView, type DiffResultViewRef } from './DiffResultView';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExecutionResultItem {
  nodeId: string;
  label: string;
  data: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  metadata?: Record<string, unknown>;
  isDiffResult?: boolean;
}

interface DataAreaProps {
  executionResults: ExecutionResultItem[];
  executionLogs: string[];
  /** Called for regular result downloads. Diff results pass `exportOverride` with original-column data. */
  onDownloadResult?: (index: number, exportOverride?: { columns: string[]; rows: string[][] }) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const INPUT_NODE_TYPES = new Set(['load_excel', 'load_csv', 'load_api']);

function formatCellValue(value: unknown): string {
  if (value instanceof ArrayBuffer) return '[Binary]';
  if (ArrayBuffer.isView(value)) return '[Binary]';
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    if (value.length > 100 && /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(value)) return '[Binary]';
    return value;
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('w' in obj && typeof obj.w === 'string') return obj.w;
    if ('v' in obj) {
      const val = obj.v;
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      if (val instanceof Date) return val.toISOString().split('T')[0];
      return String(val);
    }
    try {
      const s = JSON.stringify(value);
      return s.length > 60 ? s.substring(0, 60) + '…' : s;
    } catch {
      return '[Object]';
    }
  }
  return String(value);
}

// ── Data Table ────────────────────────────────────────────────────────────────

export function DataTable({ data, columns }: { data: Record<string, unknown>[]; columns: string[] }) {
  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center px-6">
        <Table className="w-8 h-8 text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-400">暂无数据</p>
      </div>
    );
  }
  return (
    <table className="w-full text-xs border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className="bg-zinc-50">
          <th className="w-10 px-3 py-2 text-right font-medium text-zinc-400 border-b border-r border-zinc-200 bg-zinc-50 select-none">#</th>
          {columns.map((col) => (
            <th key={col} className="px-3 py-2 text-left font-semibold text-zinc-600 border-b border-r border-zinc-200 whitespace-nowrap bg-zinc-50 last:border-r-0">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} className={`hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/40'}`}>
            <td className="px-3 py-1.5 text-zinc-400 border-b border-r border-zinc-100 text-right select-none tabular-nums">{idx + 1}</td>
            {columns.map((col) => (
              <td key={col} className="px-3 py-1.5 text-zinc-700 border-b border-r border-zinc-100 whitespace-nowrap last:border-r-0 max-w-[220px] truncate" title={formatCellValue(row[col])}>
                {formatCellValue(row[col])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, description, animated = false }: { icon: React.ElementType; title: string; description: string; animated?: boolean }) {
  const el = (
    <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-zinc-300" />
    </div>
  );
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center px-6">
      {animated ? (
        <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>{el}</motion.div>
      ) : el}
      <p className="text-sm text-zinc-500 mb-1">{title}</p>
      <p className="text-xs text-zinc-400">{description}</p>
    </div>
  );
}

// ── Tab Scrollbar ─────────────────────────────────────────────────────────────

function TabBar({ children }: { children: React.ReactNode }) {
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const onScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -100, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 100, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative flex items-center flex-1 min-w-0 overflow-hidden">
      {showLeft && (
        <button 
          onClick={scrollLeft}
          className="absolute left-0 z-10 h-full px-1 bg-gradient-to-r from-zinc-50 to-transparent text-zinc-400 hover:text-zinc-700"
        >
          <CaretLeft className="w-3 h-3" weight="bold" />
        </button>
      )}
      <div
        ref={(el) => { scrollRef.current = el; onScroll(el); }}
        onScroll={(e) => onScroll(e.currentTarget)}
        className="flex items-center gap-0.5 overflow-x-auto scrollbar-none px-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </div>
      {showRight && (
        <button 
          onClick={scrollRight}
          className="absolute right-0 z-10 h-full px-1 bg-gradient-to-l from-zinc-50 to-transparent text-zinc-400 hover:text-zinc-700"
        >
          <CaretRight className="w-3 h-3" weight="bold" />
        </button>
      )}
    </div>
  );
}

// ── Section Divider in Tab Bar ────────────────────────────────────────────────

function TabDivider() {
  return <div className="w-px h-4 bg-zinc-200 mx-1 flex-shrink-0" />;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function DataArea({ executionResults, executionLogs, onDownloadResult }: DataAreaProps) {
  const { files } = useUploadedFileStore();
  const { nodes } = useWorkflowStore();

  // Ref to the active DiffResultView so we can pull export data that reflects cell decisions
  const diffViewRef = useRef<DiffResultViewRef | null>(null);

  // Build ordered list of input nodes that have an uploaded file
  const inputTabs = useMemo(() => {
    const inputNodes = nodes.filter((n) => INPUT_NODE_TYPES.has(n.type ?? ''));
    return inputNodes
      .filter((n) => !!files[n.id])
      .map((n, idx) => ({
        id: `input_${n.id}`,
        nodeId: n.id,
        label: inputNodes.length > 1 ? `输入 #${idx + 1}` : '输入数据',
        fileName: files[n.id]!.name,
        rowCount: files[n.id]!.rowCount,
        data: files[n.id]!.data,
        columns: files[n.id]!.columns,
      }));
  }, [nodes, files]);

  // Build result tabs
  const resultTabs = useMemo(() =>
    executionResults.map((r, idx) => ({
      id: `result_${idx}`,
      index: idx,
      label: executionResults.length > 1 ? `结果 #${idx + 1}` : '执行结果',
      rowCount: r.rowCount,
      data: r.data,
      columns: r.columns,
      metadata: r.metadata,
      isDiffResult: r.isDiffResult || r.metadata?.isDiffResult === true,
    })),
    [executionResults]
  );

  // Default active tab
  const defaultTab = inputTabs[0]?.id ?? 'log';
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  // Auto-switch to first result when execution finishes
  useEffect(() => {
    if (resultTabs.length > 0) setActiveTab(resultTabs[0].id);
  }, [executionResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // If active tab was removed (e.g. file cleared), fall back
  useEffect(() => {
    const allIds = [...inputTabs.map(t => t.id), ...resultTabs.map(t => t.id), 'log'];
    if (!allIds.includes(activeTab)) {
      setActiveTab(allIds[0] ?? 'log');
    }
  }, [inputTabs, resultTabs, activeTab]);

  const activeInput  = inputTabs.find(t => t.id === activeTab);
  const activeResult = resultTabs.find(t => t.id === activeTab);
  const activeResultIndex = activeResult ? activeResult.index : -1;

  return (
    <div className="flex flex-col h-full bg-white border-t border-zinc-200">

      {/* ── Tab Bar ── */}
      <div className="flex items-center h-9 border-b border-zinc-200 bg-zinc-50/60 flex-shrink-0 overflow-hidden">
        <TabBar>
          {/* ── Input tabs ── */}
          {inputTabs.length > 0 ? (
            inputTabs.map((tab) => (
              <TabButton
                key={tab.id}
                id={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                icon={Database}
                label={tab.label}
                badge={tab.rowCount}
                badgeColor="bg-blue-100 text-blue-700"
                title={tab.fileName}
              />
            ))
          ) : (
            <TabButton
              id="input_empty"
              active={activeTab === 'input_empty'}
              onClick={() => setActiveTab('input_empty')}
              icon={Database}
              label="输入数据"
              badgeColor="bg-blue-100 text-blue-700"
            />
          )}

          {/* ── Divider ── */}
          <TabDivider />

          {/* ── Result tabs ── */}
          {resultTabs.length > 0 ? (
            resultTabs.map((tab) => (
              <TabButton
                key={tab.id}
                id={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                icon={tab.isDiffResult ? ArrowsLeftRight : Lightning}
                label={tab.label}
                badge={tab.rowCount}
                badgeColor={tab.isDiffResult ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}
              />
            ))
          ) : (
            <TabButton
              id="result_empty"
              active={activeTab === 'result_empty'}
              onClick={() => setActiveTab('result_empty')}
              icon={Lightning}
              label="执行结果"
              badgeColor="bg-amber-100 text-amber-700"
            />
          )}

          {/* ── Divider ── */}
          <TabDivider />

          {/* ── Log tab ── */}
          <TabButton
            id="log"
            active={activeTab === 'log'}
            onClick={() => setActiveTab('log')}
            icon={Terminal}
            label="日志"
            badge={executionLogs.length > 0 ? executionLogs.length : undefined}
            badgeColor="bg-zinc-100 text-zinc-600"
          />
        </TabBar>

        {/* ── Right-side contextual info ── */}
        <div className="flex items-center gap-2 pr-2 pl-1 flex-shrink-0">
          {activeInput && (
            <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
              {Math.min(activeInput.data.length, 500).toLocaleString()} / {activeInput.rowCount.toLocaleString()} 行
            </span>
          )}
          {activeResult && (
            <>
              <span className="text-[10px] text-emerald-600 font-medium tabular-nums whitespace-nowrap">
                共 {activeResult.rowCount.toLocaleString()} 行
              </span>
              {onDownloadResult && (
                <button
                  onClick={() => {
                    const exportOverride =
                      activeResult?.isDiffResult
                        ? diffViewRef.current?.getExportData()
                        : undefined;
                    onDownloadResult(activeResultIndex, exportOverride);
                  }}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors border border-zinc-200 hover:border-emerald-200"
                >
                  <DownloadSimple className="w-3 h-3" weight="bold" />
                  下载
                </button>
              )}
            </>
          )}
          {activeTab === 'log' && executionLogs.length > 0 && (
            <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
              {executionLogs.length} 条记录
            </span>
          )}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="flex-1 min-h-0 overflow-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="h-full"
          >
            {/* Input tab content */}
            {activeInput ? (
              <DataTable data={activeInput.data} columns={activeInput.columns} />
            ) : activeTab.startsWith('input_') ? (
              <EmptyState icon={Database} title="暂无输入数据" description="请在画布中添加「加载 Excel」或「加载 CSV」节点并上传文件" animated />
            ) : null}

            {/* Result tab content */}
            {activeResult ? (
              activeResult.isDiffResult ? (
                <DiffResultView
                  ref={diffViewRef}
                  data={activeResult.data}
                  columns={activeResult.columns}
                  metadata={activeResult.metadata}
                />
              ) : (
                <DataTable data={activeResult.data} columns={activeResult.columns} />
              )
            ) : (activeTab === 'result_empty' || activeTab.startsWith('result_')) && !activeResult ? (
              <EmptyState icon={Lightning} title="等待执行" description="点击顶部「▶ 运行」按钮执行工作流" />
            ) : null}

            {/* Log tab content */}
            {activeTab === 'log' && (
              executionLogs.length > 0 ? (
                <div className="min-h-full bg-zinc-950 p-3 font-mono text-xs leading-5 text-zinc-300">
                  {executionLogs.map((line, idx) => (
                    <div key={idx} className="flex gap-3 py-0.5">
                      <span className="text-zinc-600 select-none tabular-nums w-6 text-right flex-shrink-0">{idx + 1}</span>
                      <span className={
                        line.includes('[ERROR]')   ? 'text-red-400' :
                        line.includes('[SUCCESS]') ? 'text-emerald-400' :
                        line.includes('[WARN]')    ? 'text-amber-400' :
                        'text-zinc-300'
                      }>{line}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Terminal} title="暂无执行日志" description="运行工作流后将显示详细的执行过程" />
              )
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Tab Button ────────────────────────────────────────────────────────────────

function TabButton({
  id,
  active,
  onClick,
  icon: Icon,
  label,
  badge,
  badgeColor,
  title,
}: {
  id: string;
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  badge?: number | null;
  badgeColor: string;
  title?: string;
}) {
  void id;
  return (
    <button
      onClick={onClick}
      title={title}
      className={[
        'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0',
        active
          ? 'bg-white text-zinc-900 shadow-sm border border-zinc-200'
          : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/60',
      ].join(' ')}
    >
      <Icon className="w-3.5 h-3.5" weight={active ? 'fill' : 'regular'} />
      {label}
      {badge !== null && badge !== undefined && (
        <span className={`px-1.5 py-px rounded text-[10px] font-semibold ${badgeColor}`}>
          {badge.toLocaleString()}
        </span>
      )}
    </button>
  );
}
